import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, UserPlus, MessageCircle, Brain, BarChart3 } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const steps = [
  { icon: UserPlus,       step: "01", title: "Register Your Organization",  description: "Set up your account in minutes. Simply provide the email domain(s) used by your team, and we will integrate them into the platform, securely linking users to your organization." },
  { icon: Brain,          step: "02", title: "AI Assesses Every Learner",    description: "Each participant completes a brief AI-powered assessment determining their CEFR level (A1–C2) and key improvement areas." },
  { icon: UserPlus,       step: "03", title: "Smart Partner Matching",       description: "Our algorithm pairs participants by language goals, proficiency, availability, and interests — every match is productive." },
  { icon: MessageCircle,  step: "04", title: "Live 50/50 Exchange Sessions or Professional Instructor-Led Sessions",          description: "Participants can connect either online or in person. In Live 50/50 sessions, time is equally divided between both languages, while sessions led by professional instructors are conducted entirely by the teacher. An AI coach provides real-time guidance throughout each session."},
  { icon: Brain,          step: "05", title: "AI Reviews & Recommends",      description: "After each session, AI highlights achievements, corrects errors, assigns vocabulary exercises, and suggests next-session topics." },
  { icon: BarChart3,      step: "06", title: "Evaluation Reports Provided",   description: "Participants assess one another and deliver structured feedback following each exchange session." },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-background">

      <section className="pt-28 pb-16 bg-surface border-b border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">The Process</p>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
            How Lingua2 Works
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed mb-8">
            Six simple steps that transform language learning into a measurable organizational capability.
          </p>
          <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
            <Link to="/contact">Start in 48 Hours <ArrowRight className="ml-2 w-4 h-4" /></Link>
          </Button>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {steps.map((step) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="shrink-0 w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-sm font-display font-bold text-primary-foreground">{step.step}</span>
                </div>
                <div className="flex-1 pt-1">
                  <h2 className="text-lg font-display font-bold text-foreground mb-2">{step.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Button size="lg" asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Contact Us<ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 bg-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Core Principles" title="Why Language Exchange Works" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { title: "Authentic Communication", desc: "Real conversations with real people create genuine motivation and force real language use." },
              { title: "Mutual Investment",        desc: "Both participants are simultaneously learner and teacher — creating accountability on both sides." },
              { title: "Cultural Fluency",         desc: "Language exchange naturally transfers cultural knowledge and social context that no app can replicate." },
            ].map((p) => (
              <div key={p.title} className="p-6 rounded-xl border border-border bg-card">
                <h3 className="text-sm font-display font-bold text-foreground mb-3">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 transition-fast">
              <Link to="/contact">Get Started Today <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
